import { atom, useAtomValue, useSetAtom } from "jotai";
import { atomWithStorage } from "jotai/utils";
import type { MeResp } from "@tubo/api-models/hooks/common.schemas";

type AuthStatus = "unknown" | "authenticated" | "anonymous" | "unavailable";

// --- Primitive atoms (memory-only) ---

const authTokenAtom = atom<string | null>(null);

const authUserAtom = atom<MeResp | null>(null);

const authStatusAtom = atom<AuthStatus>("unknown");

// 当前租户 id（localStorage 持久化）：浏览器刷新后 beforeLoad 带租户刷新，
// 否则 /auth/refresh 不带 tenantId 会回退到用户默认租户，切换租户后刷新即丢失。
// getOnInit 必须开启：本 atom 无组件订阅、只被裸 store.get 读取（__root beforeLoad /
// 401 拦截器），不触发 mount，不开则 localStorage 永远不会被读入，刷新后恒为 null
const activeTenantIdAtom = atomWithStorage<string | null>(
  "active-tenant-id",
  null,
  undefined,
  { getOnInit: true },
);

// --- Derived atoms ---

const isAuthenticatedAtom = atom((get) => get(authTokenAtom) !== null);

// --- Action atoms ---

const setAuthAtom = atom(
  null,
  (_get, set, params: { token: string; me?: MeResp }) => {
    set(authTokenAtom, params.token);
    if (params.me !== undefined) {
      set(authUserAtom, params.me);
      const tenantId = params.me.tenant?.id;
      if (tenantId) {
        set(activeTenantIdAtom, tenantId);
      }
    }
    set(authStatusAtom, "authenticated");
  },
);

const clearAuthAtom = atom(null, (_get, set) => {
  set(authTokenAtom, null);
  set(authUserAtom, null);
  set(activeTenantIdAtom, null);
  set(authStatusAtom, "anonymous");
});

const markAuthUnavailableAtom = atom(null, (_get, set) => {
  set(authStatusAtom, "unavailable");
});

// --- Convenience hooks ---

function useAuthState() {
  return {
    token: useAtomValue(authTokenAtom),
    user: useAtomValue(authUserAtom),
    status: useAtomValue(authStatusAtom),
    isAuthenticated: useAtomValue(isAuthenticatedAtom),
  };
}

function useAuthActions() {
  return {
    setAuth: useSetAtom(setAuthAtom),
    clearAuth: useSetAtom(clearAuthAtom),
    markAuthUnavailable: useSetAtom(markAuthUnavailableAtom),
  };
}

export {
  type AuthStatus,
  authTokenAtom,
  authUserAtom,
  authStatusAtom,
  activeTenantIdAtom,
  setAuthAtom,
  clearAuthAtom,
  markAuthUnavailableAtom,
  isAuthenticatedAtom,
  useAuthState,
  useAuthActions,
};
